import React from "react";
import {format} from "date-fns";
import type {HtmlMetaDescriptor} from "@remix-run/react";
import {useLoaderData, useNavigate, useOutletContext} from "@remix-run/react";
import {json} from "@remix-run/node";
import type {LoaderFunction, MetaFunction} from "@remix-run/node";
import invariant from "tiny-invariant";
import clsx from "clsx";
import {
    Avatar,
    BackButton,
    Breadcrumbs,
    OffersSlider,
    PostCard,
    TableOfContents
} from "@bettorsignals/ui";
import {Collection, isPost, isUser, Status} from "@bettorsignals/api";
import type {Post, User} from "@bettorsignals/api";
import type {TableOfContentsType} from "~/utils/getTableOfContentFromHTML";
import {getHeadingsDataFromHtml, injectHeadingsIds} from "~/utils/getTableOfContentFromHTML";
import {LinkTo} from "~/utils/LinkTo";
import {$api} from "~/api";
import {useLinkTo} from "~/hooks/useLinkTo";
import type {WithOffersOutletContext} from "~/routes/__with-offers";

interface LoaderData {
    post: Post & {author: User};
    tableOfContents: TableOfContentsType;
    recentPosts: Post[];
}

export const loader: LoaderFunction = async ({params}) => {
    const postResponse = await $api.items(Collection.Posts).readByQuery({
        fields: ["*", "author.*"],
        filter: {
            status: {
                _eq: Status.Published
            },
            slug: {
                _eq: params.slug
            }
        }
    });

    const post: LoaderData["post"] = postResponse.data?.[0];

    if (!post) {
        throw new Response("Post Not Found", {
            status: 404
        });
    }

    invariant(isPost(post) && isUser(post.author), "Error while loading post data");

    const recentPostsResponse = await $api.items(Collection.Posts).readByQuery({
        filter: {
            status: {
                _eq: Status.Published
            },
            id: {_neq: post.id}
        },
        limit: 3,
        sort: ["-datePublished"]
    });

    return json<LoaderData>({
        post: {
            ...post,
            content: injectHeadingsIds(post.content)
        },
        tableOfContents: getHeadingsDataFromHtml(post.content),
        recentPosts: recentPostsResponse.data?.filter(isPost) ?? []
    });
};

export const meta: MetaFunction = ({data}) => {
    if (!data) {
        return {
            title: "There was an error"
        };
    }

    const {
        post: {title, metaDescription}
    } = data as LoaderData;

    const meta: HtmlMetaDescriptor = {
        title,
        description: metaDescription
    };

    return meta;
};

export default function BlogPost() {
    const {post, tableOfContents, recentPosts} = useLoaderData<LoaderData>();
    const {offers} = useOutletContext<WithOffersOutletContext>();
    const navigate = useNavigate();
    const {linkTo} = useLinkTo();

    return (
        <>
            <div className="flex flex-col gap-8">
                <Breadcrumbs
                    pages={[
                        {
                            name: "Home",
                            href: LinkTo.homePage()
                        },
                        {
                            name: "Blog",
                            href: LinkTo.blogPage()
                        },
                        {
                            name: post.title,
                            href: LinkTo.blogPostPage(post.slug)
                        }
                    ]}
                />
                <div className="md:hidden">
                    <BackButton onClick={() => navigate(-1)}>Back</BackButton>
                </div>
            </div>

            <div className="flex flex-col">
                <p className="mt-8 text-base font-normal leading-6 text-gray-500">
                    {format(new Date(post.datePublished), "MMM d, yyyy")}
                </p>
                <h1 className="mt-7 text-4xl font-extrabold leading-10 text-gray-900">
                    {post.title}
                </h1>

                <div className="mt-6 flex gap-3">
                    <Avatar
                        nickname={post.author.nickname}
                        src={
                            post.author.avatar &&
                            linkTo.asset(post.author.avatar, {
                                width: 100,
                                height: 100,
                                quality: 100,
                                seoFriendlyName: "Author avatar"
                            })
                        }
                    />
                    <div className="flex flex-col">
                        <span className="text-base font-medium leading-6 text-gray-900">
                            {post.author.nickname}
                        </span>
                        <span className="text-sm font-medium leading-5 text-gray-600">
                            {post.author.title}
                        </span>
                    </div>
                </div>
            </div>

            {post.cover && (
                <img
                    className="min-h-[152px] object-cover"
                    src={linkTo.asset(post.cover, {
                        width: 800,
                        quality: 100,
                        seoFriendlyName: `${post.title} cover image`
                    })}
                    alt={`${post.title} cover image`}
                />
            )}

            {tableOfContents.length > 0 && <TableOfContents tableOfContents={tableOfContents} />}

            <div
                //prettier-plugin-tailwindcss has troubles with prose-{element} classes
                className={clsx("prose mt-4 max-w-none", "prose-a:text-bs-purple")}
                dangerouslySetInnerHTML={{__html: post.content}}
            />

            {offers.length > 0 && (
                <div className="mt-16 block md:hidden">
                    <OffersSlider offers={offers} />
                </div>
            )}

            <div className="mt-16 mb-20 md:col-span-12">
                <h3 className="mb-12 text-3xl font-extrabold leading-9 md:text-4xl md:leading-10">
                    Recent posts
                </h3>
                <div className="border-t-2 border-gray-200">
                    <div className="mt-12 flex flex-col gap-16 sm:gap-[30px] md:flex-row md:justify-center">
                        {recentPosts.map((post) => (
                            <PostCard
                                key={post.id}
                                date={format(new Date(post.datePublished), "MMM d, yyyy")}
                                title={post.title}
                                description={post.contentPreview}
                                linkCta="Read full post"
                                href={`/blog/${post.slug}`}
                            />
                        ))}
                    </div>
                </div>
            </div>
        </>
    );
}
